import React, { useCallback, useEffect, useRef, useState } from 'react'

import DateInput from '../../components/DateInput'
import GradientButton from '../../components/GradientButton'
import { useLocale, useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { Lot } from '../../types/lot'
import { dexieLotRepo } from '../../offline/repos/DexieLotRepo'
import { lotApi } from '../../utils/lotApi'
import {
  nextLifeStage,
  readEggArrival,
  readLifeStage,
  withEggArrival,
  withLifeStage,
  type HatcheryCaution,
  type HatcheryMilestone,
  type LotLifeStage
} from '../../utils/hatchery'
import { loadLotHatcheryViews, type LotHatcheryRow } from '../../utils/reportDatasets'
import { CAPACITY_STATUS_COLOR, capacityStatusWordKey, statusAgainstMinimum } from '../../utils/carryingCapacity'
import { lotCostEventApi } from '../../utils/lotCostEventApi'
import { refreshLotSourcesFromServer } from '../../utils/refreshLotSources'
import { costPerThousandFry, formatCostEventDisplayAmount, sumCostEventsByCurrency } from './lotCostForm'
import {
  arrivalInputStyle,
  EggArrivalFields,
  ownArrival,
  parseArrivalDraft,
  toArrivalDraft,
  TRAIT_KEY,
  type ArrivalDraft
} from './eggArrivalForm'

// Hatchery v0 (findings #84): a lot that arrived as eyed eggs. Shows TUA,
// the expected hatch and first-feeding dates, survival, density and flow —
// instead of the growth/feed projection, which starts at first feeding.
// The office records each stage change with its real date (user decision
// 2026-09-29) and the egg arrival record (GLOBALG.A.P. 20.01.05 / 20.02.05,
// BAP Hatchery T4 / 4.7, ASC Trout P7).

export const LIFE_STAGE_LABEL_KEY: Record<LotLifeStage, TranslationKey> = {
  eggs: 'lots.hatchery.stage.eggs',
  yolkSac: 'lots.hatchery.stage.yolkSac',
  firstFeeding: 'lots.hatchery.stage.firstFeeding'
}

const RECORD_STAGE_KEY: Record<LotLifeStage, TranslationKey> = {
  eggs: 'lots.hatchery.record.eggs',
  yolkSac: 'lots.hatchery.record.yolkSac',
  firstFeeding: 'lots.hatchery.record.firstFeeding'
}

const CAUTION_KEY: Record<HatcheryCaution, TranslationKey> = {
  noArrivalDate: 'lots.hatchery.caution.noArrivalDate',
  noEggCount: 'lots.hatchery.caution.noEggCount',
  noTemperature: 'lots.hatchery.caution.noTemperature',
  fewTemperatureDays: 'lots.hatchery.caution.fewTemperatureDays',
  noMortalityRecords: 'lots.hatchery.caution.noMortalityRecords',
  lowMortalityCompleteness: 'lots.hatchery.caution.lowMortalityCompleteness',
  noTargets: 'lots.hatchery.caution.noTargets'
}

const labelStyle: React.CSSProperties = { fontSize: 12, color: '#0369a1' }
const valueStyle: React.CSSProperties = { fontSize: 14, color: '#0f172a', fontWeight: 700 }
const inputStyle = arrivalInputStyle

const localIsoDay = (date: Date): string => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
)

export type LotHatcherySectionProps = {
  lot: Lot
  canEdit: boolean
  /** Called after a save so the Lotes screen can refresh its lot list. */
  onLotChanged?: () => void
}

export default function LotHatcherySection({ lot, canEdit, onLotChanged }: LotHatcherySectionProps) {
  const t = useT()
  const locale = useLocale()
  const [row, setRow] = useState<LotHatcheryRow | null>(null)
  // The farm's day (not UTC: after 19:00 in Peru UTC is already tomorrow);
  // the device's local day until the row has loaded.
  const today = row?.today ?? localIsoDay(new Date())
  const [loading, setLoading] = useState(true)
  const [currentLot, setCurrentLot] = useState<Lot>(lot)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<ArrivalDraft | null>(null)
  const [stageDraftDate, setStageDraftDate] = useState<string | null>(null)
  const [applyToSiblings, setApplyToSiblings] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [costTotals, setCostTotals] = useState<Array<[string, number]> | null>(null)
  // Findings #131: the whole lot (this incubator + its siblings from the same
  // arrival) — costs of all of them ÷ live fry of all of them.
  const [wholeLot, setWholeLot] = useState<{ totals: Array<[string, number]>; alive: number | null; units: number } | null>(null)
  const [sourcesVersion, setSourcesVersion] = useState(0)

  // findings #102: cost per 1,000 fry = the lot's own costs ÷ live fry / 1,000.
  useEffect(() => {
    let cancelled = false
    void lotCostEventApi.list({ lotId: lot.id })
      .then((events) => {
        if (!cancelled) setCostTotals(sumCostEventsByCurrency(events))
      })
      .catch(() => {
        if (!cancelled) setCostTotals(null)
      })
    return () => {
      cancelled = true
    }
  }, [lot.id])

  const siblingIdsKey = row?.siblingLotIds.join('|') ?? ''
  useEffect(() => {
    const siblingIds = siblingIdsKey ? siblingIdsKey.split('|') : []
    if (siblingIds.length === 0) {
      setWholeLot(null)
      return
    }
    let cancelled = false
    const lotIds = [lot.id, ...siblingIds]
    void Promise.all([
      Promise.all(lotIds.map((lotId) => lotCostEventApi.list({ lotId }))),
      loadLotHatcheryViews({ lotIds })
    ])
      .then(([eventLists, views]) => {
        if (cancelled) return
        const alive = views.reduce<number | null>((sum, entry) => {
          const value = entry?.view?.aliveNow
          return typeof value === 'number' ? (sum ?? 0) + value : sum
        }, null)
        setWholeLot({ totals: sumCostEventsByCurrency(eventLists.flat()), alive, units: lotIds.length })
      })
      .catch(() => {
        if (!cancelled) setWholeLot(null)
      })
    return () => {
      cancelled = true
    }
  }, [lot.id, siblingIdsKey, sourcesVersion])

  // Findings #157: show the cached figures at once, then pull the crew's
  // latest records from the server and recalculate.
  const loadSeqRef = useRef(0)
  const load = useCallback(async (fromServer = false) => {
    const seq = loadSeqRef.current + 1
    loadSeqRef.current = seq
    setLoading(true)
    try {
      const [loaded] = await loadLotHatcheryViews({ lotIds: [lot.id] })
      if (loadSeqRef.current === seq) setRow(loaded ?? null)
    } catch (loadError) {
      console.warn('[lots] hatchery view failed', loadError)
      if (loadSeqRef.current === seq) setRow(null)
    } finally {
      if (loadSeqRef.current === seq) setLoading(false)
    }
    if (!fromServer) return
    const pulled = await refreshLotSourcesFromServer(lot.id).catch(() => false)
    if (!pulled || loadSeqRef.current !== seq) return
    try {
      const [loaded] = await loadLotHatcheryViews({ lotIds: [lot.id] })
      if (loadSeqRef.current !== seq) return
      setRow(loaded ?? null)
      setSourcesVersion((version) => version + 1)
    } catch (loadError) {
      console.warn('[lots] hatchery view failed', loadError)
    }
  }, [lot.id])

  useEffect(() => {
    setCurrentLot(lot)
    setEditing(false)
    setStageDraftDate(null)
    setError(null)
    void load(true)
  }, [lot, load])

  const number = (value: number | null | undefined, digits = 0) => (
    value === null || value === undefined ? '—' : value.toLocaleString(locale, { maximumFractionDigits: digits })
  )
  const formatDay = (isoDay: string | null | undefined) => {
    if (!isoDay) return '—'
    const date = new Date(`${isoDay.slice(0, 10)}T12:00:00Z`)
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }

  const lifeStage = row?.lifeStage ?? readLifeStage(currentLot)
  const siblingCount = row?.siblingLotIds.length ?? 0

  // Saves on this lot and, when asked, on the other lots of the same origin
  // (one egg batch spread over several incubators). The egg count stays per lot.
  const saveOnLots = async (apply: (target: Lot, isThisLot: boolean) => Lot) => {
    const updated = await lotApi.update(currentLot.id, { metadata: apply(currentLot, true).metadata })
    setCurrentLot(updated.data)
    if (applyToSiblings && row) {
      for (const siblingId of row.siblingLotIds) {
        const sibling = await dexieLotRepo.getById(siblingId)
        if (sibling) await lotApi.update(sibling.id, { metadata: apply(sibling, false).metadata })
      }
    }
    onLotChanged?.()
    await load()
  }

  const handleRecordStage = async (stage: LotLifeStage) => {
    if (!stageDraftDate) return
    if (row?.arrivalDate && stageDraftDate < row.arrivalDate) {
      setError(t('lots.hatchery.error.stageBeforeArrival'))
      return
    }
    if (stageDraftDate > today) {
      setError(t('lots.hatchery.error.futureDate'))
      return
    }
    setError(null)
    setSaving(true)
    try {
      const recordedAt = new Date().toISOString()
      await saveOnLots((target) => withLifeStage(target, stage, stageDraftDate, recordedAt))
      setStageDraftDate(null)
    } catch (saveError) {
      console.warn('[lots] failed to record stage', saveError)
      setError(t('lots.hatchery.error.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleSaveArrival = async () => {
    if (!draft) return
    const parsed = parseArrivalDraft(draft)
    if (!parsed.ok) {
      setError(t(parsed.errorKey))
      return
    }
    setError(null)
    setSaving(true)
    const { shared } = parsed.value
    try {
      const recordedAt = new Date().toISOString()
      const updated = await lotApi.update(currentLot.id, {
        stockingDate: draft.arrivalDate,
        metadata: withEggArrival(currentLot, {
          arrival: ownArrival(parsed.value),
          supplier: draft.supplier,
          strain: draft.strain,
          arrivalDate: draft.arrivalDate,
          recordedAt
        }).metadata
      })
      setCurrentLot(updated.data)
      if (applyToSiblings && row) {
        for (const siblingId of row.siblingLotIds) {
          const sibling = await dexieLotRepo.getById(siblingId)
          if (!sibling) continue
          const own = readEggArrival(sibling)
          await lotApi.update(sibling.id, {
            metadata: withEggArrival(sibling, {
              arrival: { ...shared, eggsReceived: own.eggsReceived, eggVolumeLitres: own.eggVolumeLitres, countFromVolume: own.countFromVolume },
              supplier: draft.supplier,
              strain: draft.strain,
              arrivalDate: draft.arrivalDate,
              recordedAt
            }).metadata
          })
        }
      }
      setEditing(false)
      onLotChanged?.()
      await load()
    } catch (saveError) {
      console.warn('[lots] failed to save egg arrival', saveError)
      setError(t('lots.hatchery.error.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const openEditor = async () => {
    const source = row && row.sourceLotId !== currentLot.id
      ? (await dexieLotRepo.getById(row.sourceLotId)) ?? currentLot
      : currentLot
    setDraft(toArrivalDraft(currentLot, source, today))
    setError(null)
    setEditing(true)
  }

  const siblingsToggle = siblingCount > 0 ? (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#334155' }}>
      <input
        type="checkbox"
        checked={applyToSiblings}
        onChange={(event) => setApplyToSiblings(event.target.checked)}
        data-testid="lot-hatchery-apply-siblings"
      />
      {t('lots.hatchery.applyToSiblings', { count: siblingCount })}
    </label>
  ) : null

  const renderMilestone = (titleKey: TranslationKey, milestone: HatcheryMilestone, testId: string) => (
    <div style={{ display: 'grid', gap: 2 }} data-testid={testId}>
      <span style={labelStyle}>{t(titleKey)}</span>
      <span style={valueStyle}>
        {milestone.recordedDate
          ? t('lots.hatchery.milestone.recorded', { date: formatDay(milestone.recordedDate), tua: number(milestone.tuaAtRecordedDate) })
          : milestone.targetTua === null
            ? t('lots.hatchery.milestone.noTarget')
            : milestone.due
              ? t('lots.hatchery.milestone.due', { tua: number(milestone.targetTua) })
              : milestone.expectedDate
                ? t('lots.hatchery.milestone.expected', { date: formatDay(milestone.expectedDate), days: number(milestone.daysToGo), tua: number(milestone.targetTua) })
                : t('lots.hatchery.milestone.target', { tua: number(milestone.targetTua) })}
      </span>
    </div>
  )

  const editor = editing && draft ? (
    <div data-testid="lot-hatchery-editor" style={{ display: 'grid', gap: 10, padding: 10, borderRadius: 8, backgroundColor: 'white', border: '1px solid #bae6fd' }}>
      <strong style={{ fontSize: 13 }}>{t('lots.hatchery.arrival.title')}</strong>
      <EggArrivalFields draft={draft} onChange={(update) => setDraft((current) => (current ? update(current) : current))} />
      {siblingsToggle}
      <div style={{ display: 'flex', gap: 8 }}>
        <GradientButton size="xs" variant="success" onClick={() => void handleSaveArrival()} disabled={saving} data-testid="lot-hatchery-save">
          {t('common.save')}
        </GradientButton>
        <GradientButton size="xs" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
          {t('common.cancel')}
        </GradientButton>
      </div>
    </div>
  ) : null

  const errorBox = error ? <div role="alert" style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{error}</div> : null

  // A fish lot with no egg record: only the entry point.
  if (!lifeStage && !loading) {
    if (!canEdit) return null
    return (
      <div data-testid="lot-hatchery-start" style={{ marginBottom: 12, display: 'grid', gap: 8 }}>
        {editing ? editor : (
          <div style={{ fontSize: 12, color: '#475569', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{t('lots.hatchery.start.question')}</span>
            <GradientButton size="xs" variant="info" onClick={() => void openEditor()} data-testid="lot-hatchery-start-button">
              {t('lots.hatchery.start.button')}
            </GradientButton>
          </div>
        )}
        {errorBox}
      </div>
    )
  }

  const view = row?.view
  const next = nextLifeStage(lifeStage)
  const nextDue = next === 'yolkSac' ? view?.hatch.due : next === 'firstFeeding' ? view?.firstFeeding.due : false
  const arrival = row?.arrival ?? {}

  return (
    <div
      data-testid="lot-hatchery-section"
      style={{ marginBottom: 12, padding: 12, borderRadius: 10, border: '1px solid #bae6fd', backgroundColor: '#f0f9ff', display: 'grid', gap: 10 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 13, color: '#0369a1' }}>{t('lots.hatchery.title')}</strong>
        {lifeStage ? (
          <span data-testid="lot-hatchery-stage" style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: 999 }}>
            {t(LIFE_STAGE_LABEL_KEY[lifeStage])}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.hatchery.loading')}</div>
      ) : !row || !view ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.hatchery.unavailable')}</div>
      ) : (
        <>
          {lifeStage !== 'firstFeeding' ? (
            <div style={{ fontSize: 12, color: '#475569' }}>{t('lots.hatchery.noProjectionYet')}</div>
          ) : null}

          <div data-testid="lot-hatchery-stages" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#334155' }}>
            <span>{t('lots.hatchery.stageLine.eggs', { date: formatDay(row.arrivalDate) })}</span>
            {row.hatchDate ? <span>· {t('lots.hatchery.stageLine.yolkSac', { date: formatDay(row.hatchDate) })}</span> : null}
            {row.firstFeedingDate ? <span>· {t('lots.hatchery.stageLine.firstFeeding', { date: formatDay(row.firstFeedingDate) })}</span> : null}
          </div>

          {next && canEdit && row.sourceLotId === currentLot.id ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: nextDue ? 8 : 0, borderRadius: 8, backgroundColor: nextDue ? '#fef9c3' : 'transparent' }}>
              {nextDue ? <strong style={{ fontSize: 12, color: '#854d0e' }}>{t('lots.hatchery.dueNotice')}</strong> : null}
              {stageDraftDate !== null ? (
                <>
                  <span style={{ fontSize: 12 }}>{t('lots.hatchery.stageDate')}</span>
                  <DateInput value={stageDraftDate} onChangeValue={setStageDraftDate} pickerOnly style={{ ...inputStyle, width: 150 }} />
                  <GradientButton size="xs" variant="success" onClick={() => void handleRecordStage(next)} disabled={saving} data-testid="lot-hatchery-stage-save">
                    {t('common.save')}
                  </GradientButton>
                  <GradientButton size="xs" variant="secondary" onClick={() => setStageDraftDate(null)} disabled={saving}>
                    {t('common.cancel')}
                  </GradientButton>
                  {siblingsToggle}
                </>
              ) : (
                <GradientButton size="xs" variant="info" onClick={() => { setError(null); setStageDraftDate(today) }} data-testid="lot-hatchery-record-stage">
                  {t(RECORD_STAGE_KEY[next])}
                </GradientButton>
              )}
            </div>
          ) : null}

          <div data-testid="lot-hatchery-figures" style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.tuaToDate')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-tua">{number(view.tuaToDate)} °C·día</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {t('lots.hatchery.tuaDetail', { arrival: number(view.tuaAtArrival), accumulated: number(view.tuaAccumulated), days: number(view.daysSinceArrival) })}
              </span>
            </div>
            {renderMilestone('lots.hatchery.milestone.hatch', view.hatch, 'lot-hatchery-hatch')}
            {renderMilestone('lots.hatchery.milestone.firstFeeding', view.firstFeeding, 'lot-hatchery-first-feeding')}
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.alive')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-alive">
                {number(view.aliveNow)} {view.eggsReceived !== null ? t('lots.hatchery.ofReceived', { received: number(view.eggsReceived) }) : ''}
                {arrival.countFromVolume ? ` ${t('lots.hatchery.countFromVolume')}` : ''}
              </span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.survival')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-survival">
                {view.survivalToHatchPct !== null ? t('lots.hatchery.survivalToHatch', { pct: number(view.survivalToHatchPct, 1) }) : t('lots.hatchery.survivalToDate', { pct: number(view.survivalToDatePct, 1) })}
                {view.survivalToFirstFeedingPct !== null ? ` · ${t('lots.hatchery.survivalToFirstFeeding', { pct: number(view.survivalToFirstFeedingPct, 1) })}` : ''}
              </span>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {t('lots.hatchery.deaths', { eggs: number(view.eggDeaths), alevins: number(view.alevinDeaths) })}
              </span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.density')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-density">
                {view.perLitre !== null ? t('lots.hatchery.perLitre', { value: number(view.perLitre, 1) }) : t('lots.hatchery.noVolume')}
              </span>
              {view.perLitre !== null && view.aliveNow !== null && view.unitVolumeLitres ? (
                <span style={{ fontSize: 11, color: '#64748b' }} data-testid="lot-hatchery-density-detail">
                  {t('lots.hatchery.perLitreDetail', {
                    alive: number(view.aliveNow),
                    litres: number(view.unitVolumeLitres),
                    cubic: number(view.unitVolumeLitres / 1000, 2)
                  })}
                </span>
              ) : null}
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.flow')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-flow">
                {view.flowLpm !== null
                  ? t('lots.hatchery.flowValue', { flow: number(view.flowLpm, 1), perThousand: number(view.flowPerThousandLpm, 2) })
                  : t('lots.hatchery.noFlow')}
              </span>
              {view.flowDate ? <span style={{ fontSize: 11, color: '#64748b' }}>{t('lots.hatchery.flowDate', { date: formatDay(view.flowDate) })}</span> : null}
              {(() => {
                // Findings #151: per 1,000 against the lot's Capacidad de carga minimum.
                const flowStatus = statusAgainstMinimum(view.flowPerThousandLpm, row.minFlowPerThousandLpm)
                if (row.minFlowPerThousandLpm === null) return null
                return (
                  <span
                    style={{ fontSize: 11, color: flowStatus ? CAPACITY_STATUS_COLOR[flowStatus] : '#64748b', fontWeight: flowStatus && flowStatus !== 'ok' ? 700 : 400 }}
                    data-testid="lot-hatchery-flow-minimum"
                    data-status={flowStatus ?? undefined}
                  >
                    {t('lots.hatchery.flowMinimum', { minimum: number(row.minFlowPerThousandLpm, 2), table: row.capacityTableName ?? '—' })}
                    {(() => {
                      const word = capacityStatusWordKey(flowStatus, 'minimum')
                      return word ? ` · ${t(word)}` : ''
                    })()}
                  </span>
                )
              })()}
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.costPerThousand')}</span>
              {wholeLot ? (
                <span style={{ ...valueStyle, fontSize: 13 }} data-testid="lot-hatchery-cost-per-thousand-whole">
                  {t('lots.hatchery.costPerThousandWholeLot', { count: wholeLot.units })}:{' '}
                  {(() => {
                    const perThousand = costPerThousandFry(wholeLot.totals, wholeLot.alive)
                    return perThousand.length > 0
                      ? perThousand.map(([currency, cents]) => formatCostEventDisplayAmount(cents, currency)).join(' · ')
                      : '—'
                  })()}
                </span>
              ) : null}
              <span style={wholeLot ? { ...valueStyle, fontSize: 13 } : valueStyle} data-testid="lot-hatchery-cost-per-thousand">
                {wholeLot ? `${t('lots.hatchery.costPerThousandThisUnit')}: ` : ''}
                {(() => {
                  const perThousand = costPerThousandFry(costTotals ?? [], view.aliveNow)
                  return perThousand.length > 0
                    ? perThousand.map(([currency, cents]) => formatCostEventDisplayAmount(cents, currency)).join(' · ')
                    : '—'
                })()}
              </span>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {costTotals && costTotals.length > 0
                  ? t('lots.hatchery.costPerThousandDetail', { total: costTotals.map(([currency, cents]) => formatCostEventDisplayAmount(cents, currency)).join(' · ') })
                  : t('lots.hatchery.costPerThousandNone')}
              </span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.hatchery.eggSize')}</span>
              <span style={valueStyle} data-testid="lot-hatchery-egg-size">
                {view.eggWeightMg !== null
                  ? t('lots.hatchery.eggSizeValue', { diameter: number(arrival.eggDiameterMm ?? null, 1), mg: number(view.eggWeightMg, 0) })
                  : '—'}
              </span>
              {view.estimatedFryWeightG !== null ? (
                <span style={{ fontSize: 11, color: '#64748b' }} data-testid="lot-hatchery-fry-weight">
                  {t('lots.hatchery.fryWeight', { grams: number(view.estimatedFryWeightG, 3) })}
                </span>
              ) : null}
            </div>
          </div>

          <div data-testid="lot-hatchery-inputs" style={{ fontSize: 12, color: '#475569', display: 'grid', gap: 2 }}>
            <span>{t('lots.hatchery.input.temperature', { temperature: number(view.meanTemperatureC, 1), recorded: view.temperatureDaysRecorded - view.temperatureDaysBorrowed, total: number(view.daysSinceArrival) })}</span>
            {view.temperatureDaysBorrowed > 0 && row.temperatureBorrowedFrom.length > 0 ? (
              <span data-testid="lot-hatchery-temperature-borrowed">
                {t('lots.hatchery.input.temperatureBorrowed', { units: row.temperatureBorrowedFrom.join(', '), borrowed: view.temperatureDaysBorrowed, total: number(view.daysSinceArrival) })}
              </span>
            ) : null}
            <span>{t('lots.hatchery.input.mortality', { recorded: view.mortalityDaysRecorded, pct: number(view.mortalityCompletenessPct, 0) })}</span>
            <span data-testid="lot-hatchery-origin">
              {t('lots.hatchery.input.origin', {
                supplier: row.supplier ?? '—',
                lot: arrival.supplierLot ?? '—',
                certification: arrival.supplierCertification ?? '—'
              })}
              {row.strain ? ` · ${row.strain}` : ''}
              {arrival.broodstockGroup ? ` · ${t('lots.hatchery.input.broodstockGroup', { group: arrival.broodstockGroup })}` : ''}
              {arrival.spawningDate ? ` · ${t('lots.hatchery.input.spawningDate', { date: arrival.spawningDate.split('-').reverse().join('/') })}` : ''}
              {arrival.stockTraits?.length ? ` · ${arrival.stockTraits.map((trait) => t(TRAIT_KEY[trait])).join(', ')}` : ''}
            </span>
            <span>
              {t('lots.hatchery.input.health', {
                certificate: arrival.healthCertificateNo ?? '—',
                date: formatDay(arrival.healthCertificateDate),
                quarantine: arrival.quarantine ? t('lots.hatchery.yes') : t('lots.hatchery.no')
              })}
            </span>
            <span>
              {arrival.disinfectionProduct
                ? t('lots.hatchery.input.disinfection', {
                  product: arrival.disinfectionProduct,
                  ppm: number(arrival.disinfectionPpm ?? null, 1),
                  minutes: number(arrival.disinfectionMinutes ?? null, 0),
                  date: formatDay(arrival.disinfectionDate)
                })
                : t('lots.hatchery.input.noDisinfection')}
            </span>
            {row.sourceLotId !== currentLot.id ? <span>{t('lots.hatchery.inheritedOrigin')}</span> : null}
          </div>

          {view.cautions.length > 0 ? (
            <ul data-testid="lot-hatchery-cautions" style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
              {view.cautions.map((caution) => <li key={caution}>{t(CAUTION_KEY[caution])}</li>)}
            </ul>
          ) : null}

          {canEdit && row.sourceLotId === currentLot.id && !editing ? (
            <div>
              <GradientButton size="xs" variant="info" onClick={() => void openEditor()} data-testid="lot-hatchery-edit">
                {t('lots.hatchery.edit')}
              </GradientButton>
            </div>
          ) : null}
          {editor}
        </>
      )}
      {errorBox}
    </div>
  )
}
